import type { JSX, ReactNode } from 'react'

import styles from './NotesIndicator.module.css'

/**
 * Where the unread counter sits. A bare icon or a borderless (tertiary) button carries
 * it on the icon's corner; a bordered (primary or secondary) button carries it
 * on its own corner.
 */
export type NotesIndicatorAnchor = 'icon' | 'button'

export function hasUnreadNotes(unreadCount: number | null): boolean {
  return (unreadCount ?? 0) > 0
}

/** The counter text: the unread total, capped at `99+`. */
export function formatUnreadCount(count: number): string {
  return count > 99 ? '99+' : String(count)
}

export function getNotesIndicatorLabel(unreadCount: number | null): string {
  return hasUnreadNotes(unreadCount) ? `Notes, ${unreadCount} unread` : 'Notes'
}

/** Hover text with the unread Note and mention totals. */
export function getNotesUnreadTooltip(
  unreadCount: number | null,
  unreadMentionCount: number | null,
): string {
  if (!hasUnreadNotes(unreadCount)) return 'Notes'
  const notes = unreadCount ?? 0
  const mentions = unreadMentionCount ?? 0
  return `${notes} unread ${notes === 1 ? 'Note' : 'Notes'}, ${mentions} ${
    mentions === 1 ? 'mention' : 'mentions'
  }`
}

/** Presentation only: the surrounding trigger owns its label and click action. */
export function NotesIndicator({
  unreadCount,
  anchor = 'icon',
  children,
}: {
  unreadCount: number | null
  anchor?: NotesIndicatorAnchor
  children: ReactNode
}): JSX.Element {
  return (
    // `data-icon` keeps Button's direct-child icon size and colour rules
    // applying through this wrapper.
    <span data-icon="notes-indicator" className={styles[anchor]}>
      {children}
      {unreadCount !== null && unreadCount > 0 && (
        <span
          aria-hidden="true"
          className={styles.count}
          data-testid="notes-unread-indicator"
        >
          {formatUnreadCount(unreadCount)}
        </span>
      )}
    </span>
  )
}
